import {
  PropertyTypeDto,
  MappingDto,
  PropertySubTypeDto,
} from "@shared/apis/property-search/generated";
import { FindListingsAv8RequestPropertyTypesEnum } from "@shared/apis/sidekick/generated";

export interface PropertyTypeSearchResult {
  parentType: PropertyTypeDto | null;
  subType: PropertySubTypeDto | null;
  mapping: MappingDto | null;
}

export const findPropertyTypeById = (
  typeId: string,
  propertyTypes: PropertyTypeDto[]
): PropertyTypeSearchResult => {
  const parentType = propertyTypes.find((pt) => pt.nameId === typeId);
  if (parentType) {
    return { parentType, subType: null, mapping: null };
  }

  for (const propertyType of propertyTypes) {
    const subType = propertyType.subTypes?.find((st) => st.nameId === typeId);
    if (subType) {
      return { parentType: propertyType, subType, mapping: null };
    }

    const mappingSubType = propertyType.subTypes?.find((st) =>
      st.mappings?.find((m) => m.nameId === typeId)
    );
    if (mappingSubType) {
      const mapping = mappingSubType.mappings?.find((m) => m.nameId === typeId);
      if (mapping) {
        return { parentType: propertyType, subType: mappingSubType, mapping };
      }
    }
  }

  return { parentType: null, subType: null, mapping: null };
};

export const isPropertyTypeDealType = (
  typeId: string,
  dealType: "Lease" | "Sale",
  propertyTypes: PropertyTypeDto[]
): boolean => {
  const result = findPropertyTypeById(typeId, propertyTypes);

  if (!result.parentType) {
    return false;
  }

  return result.parentType.dealType === dealType;
};

/**
 * Gets the display name for a property type
 * Handles the special case where "General" subtypes should show the parent name
 * and mappings should use their specific display names
 */
export const getPropertyTypeDisplayName = (
  typeId: string,
  propertyTypes: PropertyTypeDto[]
): string => {
  const result = findPropertyTypeById(typeId, propertyTypes);

  if (!result.parentType) {
    return "";
  }

  // If it's a mapping, use the mapping's display name
  if (result.mapping) {
    return (
      result.mapping.subType?.trim() ||
      result.mapping.type ||
      result.parentType.name
    );
  }

  // If it's a subtype with name "General", show parent name
  if (result.subType) {
    return result.subType.name === "General"
      ? result.parentType.name
      : result.subType.name;
  }

  // It's a parent type
  return result.parentType.name;
};

export const isValidPropertyTypeId = (
  typeId: string,
  propertyTypes: PropertyTypeDto[]
): boolean => {
  const result = findPropertyTypeById(typeId, propertyTypes);
  return (
    !!result.parentType ||
    !!result.mapping ||
    !!result.subType ||
    Object.values(FindListingsAv8RequestPropertyTypesEnum).includes(
      typeId as FindListingsAv8RequestPropertyTypesEnum
    )
  );
};

/**
 * Normalizes property type IDs based on the user's MLS source
 * If a canonical type (mlsSource "*") is selected and there's a specific MLS mapping,
 * replace it with the MLS-specific mapping
 */
export const normalizePropertyTypeIdsByMlsSource = (
  typeIds: string[],
  propertyTypes: PropertyTypeDto[],
  userMlsSources: string[]
): string[] => {
  const normalized = typeIds.map((typeId) => {
    const result = findPropertyTypeById(typeId, propertyTypes);

    if (!result.subType) {
      return typeId;
    }

    if (result.mapping) {
      return typeId;
    }

    const sameAsMappings =
      result.subType.mappings?.filter((m) => m.connectionType === "Same As") ||
      [];

    if (sameAsMappings.length === 0) {
      return typeId;
    }

    // Find the first mapping matching the user's MLS source
    for (const mlsSource of userMlsSources) {
      const specificMapping = sameAsMappings.find(
        (m) => m.mlsSource === mlsSource
      );
      if (specificMapping) {
        return specificMapping.nameId;
      }
    }

    return typeId;
  });

  // Return only unique IDs
  return Array.from(new Set(normalized));
};

/**
 * Gets the prioritized "Same As" mapping for a subtype
 * Prioritizes mappings with specific mlsSource
 * Used for displaying the best alias in dropdown menus
 */
// export const getPrioritizedSameAsMapping = (
//   mappings: MappingDto[] | undefined
// ): MappingDto | null => {
//   if (!mappings || mappings.length === 0) {
//     return null;
//   }

//   const sameAsMappings = mappings.filter((m) => m.connectionType === "Same As");

//   if (sameAsMappings.length === 0) {
//     return null;
//   }

//   const specificMapping = sameAsMappings.find((m) => m.mlsSource !== "*");
//   return specificMapping || sameAsMappings[0];
// };

export interface ProcessedSubType {
  nameId: string;
  displayName: string;
}

export interface ProcessedPropertyType {
  name: string;
  nameId: string;
  subTypes: ProcessedSubType[];
}

export const processPropertyTypesForDisplay = (
  propertyTypes: PropertyTypeDto[]
): ProcessedPropertyType[] => {
  return propertyTypes.map((propertyType) => {
    const processedSubTypes: ProcessedSubType[] = [];
    const displayedNames = new Set<string>();

    propertyType.subTypes?.forEach((subType) => {
      if (!subType.mappings || subType.mappings.length === 0) {
        const displayName =
          subType.name === "General" ? propertyType.name : subType.name;
        if (!displayedNames.has(displayName)) {
          processedSubTypes.push({ nameId: subType.nameId, displayName });
          displayedNames.add(displayName);
        }
        return;
      }

      subType.mappings
        .filter(
          (m) =>
            (m.connectionType === "Same As" ||
              m.connectionType === "Specialization Of") &&
            m.display !== "Hidden" &&
            m.nameId
        )
        .forEach((mapping) => {
          const displayName =
            mapping.display === "Rewrite"
              ? mapping.displayRewrite?.trim() ||
                mapping.subType?.trim() ||
                mapping.type ||
                subType.name
              : mapping.subType?.trim() || mapping.type || subType.name;
          if (!displayedNames.has(displayName)) {
            processedSubTypes.push({ nameId: mapping.nameId!, displayName });
            displayedNames.add(displayName);
          }
        });
    });

    return { name: propertyType.name, nameId: propertyType.nameId, subTypes: processedSubTypes };
  });
};

export const mapCanonicalTypesToAliases = (
  typeIds: string[],
  propertyTypes: PropertyTypeDto[]
): string[] => {
  return typeIds.map((typeId) => {
    const result = findPropertyTypeById(typeId, propertyTypes);

    if (result.mapping) {
      return typeId;
    }

    if (!result.subType) {
      return typeId;
    }

    if (result.subType.mappings && result.subType.mappings.length > 0) {
      const hasOnlyAliases = result.subType.mappings.every(
        (m) => m.connectionType === "Same As"
      );

      if (!hasOnlyAliases) {
        return typeId;
      }

      return result.subType.mappings[0].nameId;
    }

    return typeId;
  });
};
